import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronDown, ChevronUp, Layers, Loader2, Plus, Save, Search, X } from "lucide-react";
import { fetchJson, type CourseDetail } from "@/lib/courses";

export interface ModuleLessonOption {
  id: string;
  title: string;
  slug: string;
  emoji?: string | null;
  estimatedMinutes?: number;
  xpReward?: number;
  grade?: number;
  subject?: string;
  topic?: string;
  status?: string;
}

interface DraftModule {
  lessonId: string;
  required: boolean;
  title: string;
  emoji: string | null;
  subtitle: string;
}

interface CourseModulesEditorProps {
  courseSlug: string;
  courseTitle: string;
  /** Lessons the caller is allowed to add (admin: every lesson, creator: own lessons). */
  lessonOptions: ModuleLessonOption[];
  lessonsLoading: boolean;
  lessonsError?: unknown;
  /** Extra controls rendered beside the lesson search box, for example a grade filter. */
  extraFilters?: React.ReactNode;
  onSave: (modules: { lessonId: string; required: boolean }[]) => void;
  saving: boolean;
}

function lessonSubtitle(l: ModuleLessonOption): string {
  const bits: string[] = [];
  if (typeof l.grade === "number") bits.push(l.grade === 0 ? "Interest" : `Grade ${l.grade}`);
  if (l.subject) bits.push(l.subject);
  if (l.topic && l.topic !== l.subject) bits.push(l.topic);
  if (typeof l.estimatedMinutes === "number") bits.push(`${l.estimatedMinutes} min`);
  if (typeof l.xpReward === "number") bits.push(`${l.xpReward} XP`);
  return bits.join(" · ");
}

/**
 * Ordered lesson list for one course, shared by the admin manager and the creator builder.
 * Reordering is up/down buttons on purpose so there is no drag-and-drop dependency.
 */
export function CourseModulesEditor({
  courseSlug,
  courseTitle,
  lessonOptions,
  lessonsLoading,
  lessonsError,
  extraFilters,
  onSave,
  saving,
}: CourseModulesEditorProps) {
  const [draft, setDraft] = useState<DraftModule[]>([]);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState(false);
  const seededRef = useRef(false);

  const { data, isLoading, error } = useQuery<CourseDetail>({
    queryKey: ["/api/courses", courseSlug, "modules"],
    queryFn: () => fetchJson<CourseDetail>(`/api/courses/${courseSlug}`),
    retry: false,
  });

  useEffect(() => {
    if (seededRef.current || !data?.modules) return;
    seededRef.current = true;
    setDraft(
      [...data.modules]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((m) => ({
          lessonId: m.lesson.id,
          required: m.required,
          title: m.lesson.title,
          emoji: m.lesson.emoji,
          subtitle: [`${m.lesson.estimatedMinutes} min`, `${m.lesson.xpReward} XP`].join(" · "),
        })),
    );
  }, [data]);

  const chosen = useMemo(() => new Set(draft.map((d) => d.lessonId)), [draft]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lessonOptions;
    return lessonOptions.filter((l) =>
      [l.title, l.slug, l.subject, l.topic].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [lessonOptions, search]);

  const visible = matches.slice(0, 40);

  const move = (index: number, delta: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const add = (lesson: ModuleLessonOption) => {
    setDraft((prev) => [
      ...prev,
      { lessonId: lesson.id, required: true, title: lesson.title, emoji: lesson.emoji ?? null, subtitle: lessonSubtitle(lesson) },
    ]);
    setDirty(true);
  };

  const remove = (lessonId: string) => {
    setDraft((prev) => prev.filter((d) => d.lessonId !== lessonId));
    setDirty(true);
  };

  const toggleRequired = (lessonId: string, required: boolean) => {
    setDraft((prev) => prev.map((d) => (d.lessonId === lessonId ? { ...d, required } : d)));
    setDirty(true);
  };

  return (
    <div className="space-y-5" data-testid="course-modules-editor">
      <SheetHeader className="space-y-1">
        <SheetTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5" /> Lessons in {courseTitle}
        </SheetTitle>
        <SheetDescription>
          The order below is the order students work through. Required lessons must be completed before the credential is
          issued.
        </SheetDescription>
      </SheetHeader>

      {error && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
          <p className="font-medium">Could not load the lessons already on this course.</p>
          <p className="text-muted-foreground">
            {(error as Error)?.message || "Unknown error"}. Anything you save here replaces the whole lesson list.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Course lessons ({draft.length})</Label>
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : draft.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center">
            <p className="font-medium text-sm">No lessons on this course yet</p>
            <p className="text-sm text-muted-foreground">Search below and add the first lesson.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {draft.map((m, i) => (
              <div key={m.lessonId} className="flex items-center gap-2 rounded-md border p-2" data-testid={`module-row-${i}`}>
                <span className="text-xs text-muted-foreground w-5 text-center tabular-nums">{i + 1}</span>
                <span className="text-lg">{m.emoji || "📘"}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  {m.subtitle && <p className="text-xs text-muted-foreground truncate">{m.subtitle}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch
                    checked={m.required}
                    onCheckedChange={(v) => toggleRequired(m.lessonId, v)}
                    aria-label="Required lesson"
                  />
                  <span className="text-xs text-muted-foreground w-16">{m.required ? "Required" : "Optional"}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled={i === draft.length - 1}
                    onClick={() => move(i, 1)}
                    title="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(m.lessonId)} title="Remove">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2 border-t pt-4">
        <Label className="text-sm font-semibold">Add lessons</Label>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search lessons by title, topic or slug"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-lesson-search"
            />
          </div>
          {extraFilters}
        </div>

        {lessonsError ? (
          <p className="text-sm text-destructive">{(lessonsError as Error)?.message || "Could not load lessons"}</p>
        ) : lessonsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No lessons match that search. Try another word or change the grade filter.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
            {visible.map((l) => {
              const added = chosen.has(l.id);
              return (
                <div key={l.id} className="flex items-center gap-2 rounded-md border p-2">
                  <span className="text-lg">{l.emoji || "📘"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{l.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{lessonSubtitle(l)}</p>
                  </div>
                  {l.status && l.status !== "published" && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {l.status}
                    </Badge>
                  )}
                  <Button size="sm" variant={added ? "ghost" : "outline"} disabled={added} onClick={() => add(l)} className="shrink-0">
                    {added ? "Added" : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
                  </Button>
                </div>
              );
            })}
            {matches.length > visible.length && (
              <p className="text-xs text-muted-foreground text-center pt-1">
                Showing {visible.length} of {matches.length}. Narrow the search to see the rest.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        <Button
          onClick={() => {
            onSave(draft.map((d) => ({ lessonId: d.lessonId, required: d.required })));
            setDirty(false);
          }}
          disabled={saving}
          data-testid="button-save-modules"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save lesson order</>}
        </Button>
      </div>
    </div>
  );
}

export default CourseModulesEditor;
